import {
  blankGenericTable,
  can,
  canReach,
  collectedAtOf,
  computeReportSections,
  defaultDueDate,
  defaultSamples,
  earliestCollected,
  isDay,
  isEditable,
  isSensitive,
  localDay,
  newId,
  nextStatuses,
  type PriceItem,
  priceItemsFor,
  type ReportContent,
  ReportContent as ReportContentSchema,
  type ReportStatus,
  reportSearchText,
  type Sample,
  type Signoff,
  sampleKindOf,
  summarizeReport,
  type TemplateDoc,
  type WorkflowLevel,
  withoutPrice,
} from '@quickreport/domain';
import { type LocalDb, queueChange, type Tx } from '@quickreport/sync';
import { recordAudit } from './audit';
import { takeLabNumber } from './lab-numbers';
import { type PatientInput, type PatientRow, savePatient, snapshotOf } from './patients';
import { rememberSampleTypes } from './sample-types';

export interface WorkspaceCtx {
  orgId: string;
  userId: string;
  memberId: string;
  roles: string[];
  deviceId?: string | null;
}

export interface SiteInfo {
  id: string;
  code: string;
  name: string;
  workflowLevel: WorkflowLevel;
  labNumberPattern: string;
}

export async function getSite(db: Pick<LocalDb, 'query'> | Tx, siteId: string): Promise<SiteInfo> {
  const r = await db.query<{
    id: string;
    code: string;
    name: string;
    workflow_level: WorkflowLevel;
    lab_number_pattern: string;
  }>('select id, code, name, workflow_level, lab_number_pattern from sites where id = $1', [
    siteId,
  ]);
  const s = r.rows[0];
  if (!s)
    throw new Error(
      'This lab site is not on this computer yet. Wait for the first sync to finish.',
    );
  return {
    id: s.id,
    code: s.code,
    name: s.name,
    workflowLevel: s.workflow_level,
    labNumberPattern: s.lab_number_pattern,
  };
}

export interface TemplateRef {
  templateId: string;
  version: number;
  doc: TemplateDoc;
}

export interface NewReportInput {
  siteId: string;
  patient: PatientInput;
  visit: { referredBy?: string; sampleType?: string; collectedAt?: string };
  /**
   * What was collected. Left out, one sample of each kind the chosen tests ask for is added,
   * collected now.
   */
  samples?: Sample[];
  /** The day the report is promised (YYYY-MM-DD). Left out: the slowest test sets it, else today. */
  dueDate?: string;
  templates: TemplateRef[];
  /** Create an "Other (generic)" report instead of, or as well as, template ones. */
  generic?: { title: string };
  /**
   * What the lab charges for the whole visit, in paise. Left out: the prices on the chosen
   * templates are added up. When given it replaces them, which is how a lab that does not price
   * each template charges for a set of tests.
   */
  chargePaise?: number;
  /** Overrides the suggested report name (only used when a single report is created). */
  name?: string;
}

export class WorkflowError extends Error {}

/** Two names, then "+ n more", then the patient. */
export function suggestName(docNames: string[], patientName: string): string {
  const head =
    docNames.length <= 2 ? docNames.join(' + ') : `${docNames[0]} + ${docNames.length - 1} more`;
  return `${head} · ${patientName}`;
}

function baseContent(
  patient: ReturnType<typeof snapshotOf>,
  input: NewReportInput,
  labNo: string,
  now: Date,
  samples: Sample[],
): Omit<ReportContent, 'sections' | 'kind' | 'title'> {
  return {
    patient,
    fields: [],
    visit: {
      visitNo: `V-${labNo}`,
      ...(input.visit.referredBy?.trim() ? { referredBy: input.visit.referredBy.trim() } : {}),
      ...(input.visit.sampleType?.trim() ? { sampleType: input.visit.sampleType.trim() } : {}),
      collectedAt: earliestCollected(samples) ?? input.visit.collectedAt ?? now.toISOString(),
    },
    samples,
  };
}

/**
 * The samples a report lists: those its own tests need. A sample that none of the visit's tests
 * ask for (typed in by hand) is listed on every report, so it is never lost.
 */
function samplesFor(all: Sample[], refs: TemplateRef[], visitRefs: TemplateRef[]): Sample[] {
  const kindsOf = (rs: TemplateRef[]) =>
    new Set(rs.map((r) => sampleKindOf(r.doc.sampleType)?.toLowerCase()).filter(Boolean));
  const mine = kindsOf(refs);
  const wanted = kindsOf(visitRefs);
  if (!mine.size) return all;
  return all.filter((s) => {
    const k = s.type.trim().toLowerCase();
    return mine.has(k) || !wanted.has(k);
  });
}

/**
 * Register a visit and create its report(s): tests marked "always separate" get their own
 * report; the rest share one. Everything happens in one local transaction and syncs later.
 */
export async function createReports(
  db: LocalDb,
  ctx: WorkspaceCtx,
  input: NewReportInput,
  now: Date = new Date(),
): Promise<string[]> {
  if (!can(ctx.roles, 'register_visit'))
    throw new WorkflowError('Your role cannot register patients.');
  if (input.templates.length === 0 && !input.generic)
    throw new WorkflowError('Choose at least one test.');
  const site = await getSite(db, input.siteId);

  return db.transaction(async (tx) => {
    const patientRow: PatientRow = await savePatient(tx, ctx.orgId, input.patient, now);
    const snap = snapshotOf(patientRow, now);

    const combined = input.templates.filter((t) => !t.doc.separateReport);
    const separate = input.templates.filter((t) => t.doc.separateReport);
    const groups: { refs: TemplateRef[]; generic?: { title: string } }[] = [];
    if (combined.length) groups.push({ refs: combined });
    for (const s of separate) groups.push({ refs: [s] });
    if (input.generic) groups.push({ refs: [], generic: input.generic });

    const visitSamples = (
      input.samples ??
      defaultSamples(
        input.templates.map((t) => t.doc),
        now,
        newId,
      )
    ).filter((x) => x.type.trim());
    await rememberSampleTypes(tx, ctx.orgId, visitSamples);

    const visitId = newId();
    const ids: string[] = [];
    let firstLabNo = '';
    const built: {
      id: string;
      labNo: string;
      name: string;
      content: ReportContent;
      items: PriceItem[];
      due: string;
    }[] = [];

    const prefixRow = await tx.query<{ p: string | null }>(
      "select doc->>'labNumberPrefix' as p from brand_settings where org_id = $1 and site_id is null and deleted_at is null limit 1",
      [ctx.orgId],
    );
    const labPrefix = (prefixRow.rows[0]?.p ?? '').trim();

    for (const g of groups) {
      const taken = await takeLabNumber(
        tx,
        ctx.orgId,
        { id: site.id, code: site.code, pattern: site.labNumberPattern },
        now,
      );
      // A lab can start its numbers with its own letters, e.g. SUN-2026-000001.
      const labNo = labPrefix ? `${labPrefix}-${taken}` : taken;
      firstLabNo ||= labNo;
      const id = newId();
      let content: ReportContent;
      let name: string;
      if (g.generic) {
        const doc = { ...blankGenericTable(), name: g.generic.title || 'Report' };
        content = ReportContentSchema.parse({
          ...baseContent(snap, input, labNo, now, visitSamples),
          kind: 'generic',
          title: g.generic.title || 'Report',
          sections: [{ doc, values: {} }],
        });
        name = suggestName([g.generic.title || 'Report'], snap.name);
      } else {
        const sampleTypes = [
          ...new Set(g.refs.map((r) => r.doc.sampleType).filter(Boolean)),
        ] as string[];
        const base = baseContent(
          snap,
          input,
          labNo,
          now,
          samplesFor(visitSamples, g.refs, input.templates),
        );
        content = ReportContentSchema.parse({
          ...base,
          visit: {
            ...base.visit,
            ...(base.visit.sampleType
              ? {}
              : sampleTypes.length
                ? { sampleType: sampleTypes.join(', ') }
                : {}),
          },
          kind: 'templated',
          sections: g.refs.map((r) => ({
            templateId: r.templateId,
            templateVersion: r.version,
            doc: withoutPrice(r.doc),
            values: {},
          })),
        });
        name = suggestName(
          g.refs.map((r) => r.doc.name),
          snap.name,
        );
      }
      if (input.name?.trim() && groups.length === 1) name = input.name.trim();
      // The price each test has today, kept with this report even if the template changes later.
      let items = priceItemsFor(
        g.refs.map((r) => ({ name: r.doc.name, price: r.doc.price, templateId: r.templateId })),
      );
      if (input.chargePaise !== undefined) {
        // One charge for the whole visit sits on the first report; the others carry no price.
        const first = ids.length === 0;
        items = first
          ? [
              {
                name: 'Charge for these tests',
                pricePaise: Math.max(0, Math.round(input.chargePaise)),
              },
            ]
          : items.map((i) => ({ ...i, pricePaise: 0 }));
      }
      const due =
        input.dueDate && isDay(input.dueDate)
          ? input.dueDate
          : defaultDueDate(
              g.refs.map((r) => r.doc),
              localDay(now),
            );
      built.push({ id, labNo, name, content, items, due });
      ids.push(id);
    }

    const collectedIso =
      earliestCollected(visitSamples) ?? input.visit.collectedAt ?? now.toISOString();
    await tx.query(
      `insert into visits (id, org_id, site_id, patient_id, visit_no, referred_by, collected_at, received_at)
       values ($1, $2, $3, $4, $5, $6, $7, $8)`,
      [
        visitId,
        ctx.orgId,
        site.id,
        patientRow.id,
        `V-${firstLabNo}`,
        input.visit.referredBy?.trim() || null,
        collectedIso,
        // Below the accredited level, collecting a sample counts as receiving it.
        site.workflowLevel === 'nabl' ? null : collectedIso,
      ],
    );
    await queueChange(tx, 'visits', visitId);

    for (const b of built) {
      await tx.query(
        `insert into reports (id, org_id, site_id, visit_id, folder_id, name, lab_no, status, content, version, search_text, sensitive, collected_at, received_at, price_paise, price_items, due_date)
         values ($1, $2, $3, $4, $5, $6, $7, 'draft', $8::jsonb, 0, $9, $10, $11, $12, $13, $14::jsonb, $15)`,
        [
          b.id,
          ctx.orgId,
          site.id,
          visitId,
          // Folders were dropped: new reports are in none. Old ones keep their value, unused.
          null,
          b.name,
          b.labNo,
          JSON.stringify(b.content),
          reportSearchText(b.name, b.labNo, b.content),
          isSensitive(b.content),
          ...visitDates(b.content),
          b.items.reduce((n, i) => n + i.pricePaise, 0),
          JSON.stringify(b.items),
          b.due,
        ],
      );
      await queueChange(tx, 'reports', b.id);
      await recordAudit(tx, ctx.orgId, ctx, {
        entity: 'report',
        entityId: b.id,
        action: 'created',
        after: { labNo: b.labNo, name: b.name },
      });
    }
    return ids;
  });
}

export interface ReportRecord {
  id: string;
  siteId: string;
  visitId: string;
  folderId: string | null;
  name: string;
  labNo: string;
  status: ReportStatus;
  version: number;
  verifyCode: string | null;
  updatedAt: string;
  /** YYYY-MM-DD, or null for reports made before due dates existed. */
  dueDate: string | null;
  dueDoneAt: string | null;
  content: ReportContent;
}

export async function loadReport(
  db: Pick<LocalDb, 'query'> | Tx,
  id: string,
): Promise<ReportRecord | null> {
  const r = await db.query<{
    id: string;
    site_id: string;
    visit_id: string;
    folder_id: string | null;
    name: string;
    lab_no: string;
    status: ReportStatus;
    version: number;
    verify_code: string | null;
    updated_at: string;
    due_date: string | null;
    due_done_at: string | null;
    content: unknown;
  }>(
    'select id, site_id, visit_id, folder_id, name, lab_no, status, version, verify_code, updated_at, due_date::text as due_date, due_done_at, content from reports where id = $1 and deleted_at is null',
    [id],
  );
  const row = r.rows[0];
  if (!row) return null;
  return {
    id: row.id,
    siteId: row.site_id,
    visitId: row.visit_id,
    folderId: row.folder_id,
    name: row.name,
    labNo: row.lab_no,
    status: row.status,
    version: row.version,
    verifyCode: row.verify_code,
    updatedAt: row.updated_at,
    dueDate: row.due_date,
    dueDoneAt: row.due_done_at,
    content: ReportContentSchema.parse(row.content),
  };
}

type Diff = { section: string; test: string; before: string; after: string };

function valueDiffs(prev: ReportContent, next: ReportContent): Diff[] {
  const out: Diff[] = [];
  next.sections.forEach((s, i) => {
    const before = prev.sections[i]?.values ?? {};
    for (const [rowId, after] of Object.entries(s.values)) {
      const was = before[rowId] ?? '';
      if (was === after) continue;
      const row = s.doc.rows.find((r) => r.id === rowId);
      out.push({
        section: s.doc.name,
        test: row && row.kind === 'test' ? row.label : rowId,
        before: was,
        after,
      });
    }
    for (const [rowId, was] of Object.entries(before)) {
      if (!(rowId in s.values) && was !== '') {
        const row = s.doc.rows.find((r) => r.id === rowId);
        out.push({
          section: s.doc.name,
          test: row && row.kind === 'test' ? row.label : rowId,
          before: was,
          after: '',
        });
      }
    }
  });
  return out;
}

/** Save an edit to an unsigned report and record every changed result in the audit trail. */
export async function saveReportContent(
  db: LocalDb,
  ctx: WorkspaceCtx,
  id: string,
  next: ReportContent,
): Promise<void> {
  const parsed = ReportContentSchema.parse(next);
  await db.transaction(async (tx) => {
    const cur = await loadReport(tx, id);
    if (!cur) throw new WorkflowError('That report no longer exists.');
    if (!isEditable(cur.status))
      throw new WorkflowError('A signed report cannot be edited. Amend it first.');
    await rememberSampleTypes(tx, ctx.orgId, parsed.samples);
    const diffs = valueDiffs(cur.content, parsed);
    if (diffs.length && !can(ctx.roles, 'enter_results'))
      throw new WorkflowError('Your role cannot enter results.');
    await tx.query(
      'update reports set content = $2::jsonb, search_text = $3, sensitive = $4, collected_at = $5, received_at = $6, updated_at = now() where id = $1',
      [
        id,
        JSON.stringify(parsed),
        reportSearchText(cur.name, cur.labNo, parsed),
        isSensitive(parsed),
        ...visitDates(parsed),
      ],
    );
    await queueChange(tx, 'reports', id);
    for (const d of diffs) {
      await recordAudit(tx, ctx.orgId, ctx, {
        entity: 'report',
        entityId: id,
        action: d.before === '' ? 'result_entered' : 'result_changed',
        before: { [`${d.section} · ${d.test}`]: d.before },
        after: { [`${d.section} · ${d.test}`]: d.after },
      });
    }
  });
}

export async function renameReport(
  db: LocalDb,
  ctx: WorkspaceCtx,
  id: string,
  name: string,
): Promise<void> {
  const n = name.trim();
  if (!n) throw new WorkflowError('A report needs a name.');
  await db.transaction(async (tx) => {
    const cur = await loadReport(tx, id);
    if (!cur) return;
    await tx.query(
      'update reports set name = $2, search_text = $3, updated_at = now() where id = $1',
      [id, n, reportSearchText(n, cur.labNo, cur.content)],
    );
    await queueChange(tx, 'reports', id);
    await recordAudit(tx, ctx.orgId, ctx, {
      entity: 'report',
      entityId: id,
      action: 'renamed',
      before: cur.name,
      after: n,
    });
  });
}

/** The sample collected and received times held inside a report, for sorting and filtering. */
export function visitDates(content: ReportContent): [string | null, string | null] {
  const ok = (v?: string) => (v && !Number.isNaN(Date.parse(v)) ? new Date(v).toISOString() : null);
  return [ok(collectedAtOf(content) ?? undefined), ok(content.visit.receivedAt)];
}

export class ReportBlockedError extends WorkflowError {
  constructor(readonly issues: string[]) {
    super(issues[0] ?? 'This report cannot be signed yet.');
  }
}

export interface TransitionOptions {
  /** Who signs, for moves to final or partial. Not needed when the lab prints no signatures. */
  signatoryId?: string | undefined;
  /** Required when amending or revoking. */
  reason?: string;
  /** The signatory has looked at the critical values. */
  criticalReviewed?: boolean;
  /** The lab's branding and signatories as they are now; kept with the signed version. */
  signoff?: Signoff;
  now?: Date;
}

/**
 * Move a report along its workflow. Signing freezes the results as a numbered version; amending
 * reopens a signed report with a recorded reason; revoking withdraws it. The server checks the
 * same rules again, so a modified client gains nothing.
 */
export async function transitionReport(
  db: LocalDb,
  ctx: WorkspaceCtx,
  id: string,
  to: ReportStatus,
  opts: TransitionOptions = {},
): Promise<void> {
  const now = opts.now ?? new Date();
  await db.transaction(async (tx) => {
    const cur = await loadReport(tx, id);
    if (!cur) throw new WorkflowError('That report no longer exists.');
    const site = await getSite(tx, cur.siteId);
    if (!nextStatuses(site.workflowLevel, cur.status, ctx.roles).includes(to)) {
      throw new WorkflowError(
        canReach(site.workflowLevel, cur.status, to, ctx.roles)
          ? 'Complete the earlier steps first.'
          : 'Your role cannot do that.',
      );
    }
    let content = cur.content;
    let version = cur.version;

    if (to === 'final' || to === 'partial') {
      const sum = summarizeReport(content);
      if (to === 'final' && sum.blocking.length)
        throw new ReportBlockedError([...new Set(sum.blocking)]);
      if (sum.critical > 0 && !opts.criticalReviewed) {
        throw new ReportBlockedError([
          `${sum.critical} critical value${sum.critical === 1 ? '' : 's'} must be reviewed before signing.`,
        ]);
      }
      const at = now.toISOString();
      content = ReportContentSchema.parse({
        ...content,
        visit: {
          ...content.visit,
          reportedAt:
            content.visit.reportedAtManual && content.visit.reportedAt
              ? content.visit.reportedAt
              : at,
        },
        ...(opts.signoff ? { signoff: opts.signoff } : {}),
        sections: content.sections.map((s) => ({
          ...s,
          ...(opts.signatoryId ? { signedBy: opts.signatoryId } : {}),
          signedAt: at,
        })),
      });
      version += 1;
      const lastAmend =
        cur.status === 'amended'
          ? ((
              await tx.query<{ reason: string | null }>(
                `select reason from audit_events where entity_id = $1 and action = 'amended' order by at desc limit 1`,
                [id],
              )
            ).rows[0]?.reason ?? null)
          : null;
      const vid = newId();
      await tx.query(
        `insert into report_versions (id, org_id, report_id, version, content, signed_by, signed_at, amend_reason)
         values ($1, $2, $3, $4, $5::jsonb, $6, $7, $8)`,
        [vid, ctx.orgId, id, version, JSON.stringify(content), ctx.memberId, at, lastAmend],
      );
      await queueChange(tx, 'report_versions', vid);
    }
    if ((to === 'amended' || to === 'revoked') && !opts.reason?.trim()) {
      throw new WorkflowError(
        `Say why you are ${to === 'amended' ? 'amending' : 'revoking'} this report.`,
      );
    }

    await tx.query(
      'update reports set status = $2, content = $3::jsonb, version = $4, search_text = $5, collected_at = $6, received_at = $7, updated_at = now() where id = $1',
      [
        id,
        to,
        JSON.stringify(content),
        version,
        reportSearchText(cur.name, cur.labNo, content),
        ...visitDates(content),
      ],
    );
    await queueChange(tx, 'reports', id);
    await recordAudit(tx, ctx.orgId, ctx, {
      entity: 'report',
      entityId: id,
      action:
        to === 'amended'
          ? 'amended'
          : to === 'revoked'
            ? 'revoked'
            : to === 'final' || to === 'partial'
              ? 'signed'
              : 'status_changed',
      before: { status: cur.status, version: cur.version },
      after: { status: to, version },
      ...(opts.reason?.trim() ? { reason: opts.reason.trim() } : {}),
    });
  });
}

export function reportComputed(content: ReportContent) {
  return computeReportSections(content);
}

// ---- lists ------------------------------------------------------------

export type ReportSort = 'updated' | 'collected' | 'received';

export interface ReportFilters {
  q?: string;
  /** Only the reports of this patient (through their visits). */
  patientId?: string;
  statuses?: ReportStatus[];
  /** The date the list is ordered by, and the one `from` and `to` apply to. */
  sort?: ReportSort;
  /** Newest first unless 'asc'. */
  order?: 'asc' | 'desc';
  /** Inclusive day limits (YYYY-MM-DD, local time) on the sorted date. */
  from?: string;
  to?: string;
  /** 'waiting' = changes not yet uploaded; 'server' = nothing waiting. */
  sync?: 'waiting' | 'server';
  /** 'with' = has a QR code; 'without' = does not have one yet. */
  qr?: 'with' | 'without';
  /** Payment state: nothing paid, partly paid, or paid in full (or more). Needs billing access. */
  pay?: 'unpaid' | 'part' | 'paid';
  page?: number;
  pageSize?: number;
}

export const DEFAULT_PAGE_SIZE = 50;

export interface ReportListItem {
  id: string;
  name: string;
  lab_no: string;
  status: ReportStatus;
  updated_at: string;
  collected_at: string | null;
  received_at: string | null;
  folder_id: string | null;
  sensitive: boolean;
  verify_code: string | null;
  patient_name: string;
  pending: boolean;
  /** Private billing figures, in paise. Only shown to people who may see billing. */
  due_paise: number;
  paid_paise: number;
  due_date: string | null;
  due_done_at: string | null;
}

const SORT_COLUMN: Record<ReportSort, string> = {
  updated: 'r.updated_at',
  collected: 'r.collected_at',
  received: 'r.received_at',
};

function reportWhere(orgId: string, f: ReportFilters): { where: string; params: unknown[] } {
  const params: unknown[] = [orgId];
  const where = ['r.org_id = $1', 'r.deleted_at is null'];
  for (const w of (f.q ?? '').toLowerCase().split(/\s+/).filter(Boolean)) {
    params.push(`%${w}%`);
    where.push(`r.search_text like $${params.length}`);
  }
  if (f.patientId) {
    params.push(f.patientId);
    where.push(`r.visit_id in (select v.id from visits v where v.patient_id = $${params.length})`);
  }
  if (f.statuses?.length) {
    params.push(f.statuses);
    where.push(`r.status::text = any($${params.length}::text[])`);
  }
  const col = SORT_COLUMN[f.sort ?? 'updated'];
  // Days are the person's local days; the device sends them as local midnight.
  if (f.from && !Number.isNaN(Date.parse(f.from))) {
    params.push(new Date(`${f.from}T00:00:00`).toISOString());
    where.push(`${col} >= $${params.length}`);
  }
  if (f.to && !Number.isNaN(Date.parse(f.to))) {
    const end = new Date(`${f.to}T00:00:00`);
    end.setDate(end.getDate() + 1);
    params.push(end.toISOString());
    where.push(`${col} < $${params.length}`);
  }
  if (f.sync === 'waiting') where.push('exists(select 1 from outbox o where o.row_id = r.id)');
  if (f.sync === 'server') where.push('not exists(select 1 from outbox o where o.row_id = r.id)');
  if (f.qr === 'with') where.push('r.verify_code is not null');
  if (f.qr === 'without') where.push('r.verify_code is null');
  if (f.pay) {
    const paid = `coalesce((select sum(p.amount_paise) from payments p where p.report_id = r.id and p.deleted_at is null), 0)`;
    const due = 'greatest(r.price_paise - r.discount_paise, 0)';
    if (f.pay === 'unpaid') where.push(`${due} > 0 and ${paid} = 0`);
    else if (f.pay === 'part') where.push(`${paid} > 0 and ${paid} < ${due}`);
    else where.push(`${due} > 0 and ${paid} >= ${due}`);
  }
  return { where: where.join(' and '), params };
}

export function reportListQuery(
  orgId: string,
  f: ReportFilters,
): { sql: string; params: unknown[] } {
  const { where, params } = reportWhere(orgId, f);
  const size = Math.min(Math.max(Math.floor(f.pageSize ?? DEFAULT_PAGE_SIZE), 1), 500);
  const offset = (Math.max(Math.floor(f.page ?? 1), 1) - 1) * size;
  const col = SORT_COLUMN[f.sort ?? 'updated'];
  const dir = f.order === 'asc' ? 'asc' : 'desc';
  return {
    sql: `select r.id, r.name, r.lab_no, r.status, r.updated_at, r.collected_at, r.received_at,
                 r.folder_id, r.sensitive, r.verify_code,
                 r.content->'patient'->>'name' as patient_name,
                 exists(select 1 from outbox o where o.row_id = r.id) as pending,
                 r.due_date::text as due_date, r.due_done_at,
                 greatest(r.price_paise - r.discount_paise, 0)::int as due_paise,
                 coalesce((select sum(p.amount_paise) from payments p
                            where p.report_id = r.id and p.deleted_at is null), 0)::int as paid_paise
            from reports r where ${where}
           order by ${col} ${dir} nulls last, r.id ${dir}
           limit ${size} offset ${offset}`,
    params,
  };
}

/** How many reports match, ignoring the page. */
export function reportCountQuery(
  orgId: string,
  f: ReportFilters,
): { sql: string; params: unknown[] } {
  const { where, params } = reportWhere(orgId, f);
  return { sql: `select count(*)::int as n from reports r where ${where}`, params };
}
